import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';

export interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Signed-in user's email, shown in the drawer footer. */
  email: string;
  isAdmin: boolean;
  /** Show the "Crew" badge next to the email. */
  isCrew: boolean;
  /** Owned by App.tsx — the drawer never duplicates the sign-out logic. */
  signOut: () => void | Promise<void>;
}

const PRIMARY_LINKS = [
  { to: '/schedule', label: 'Schedule' },
  { to: '/project-schedule', label: 'By Project' },
  { to: '/me', label: 'My Schedule' },
  { to: '/projects', label: 'Projects' },
];

const ADMIN_LINKS = [
  { to: '/admin/categories', label: 'Categories' },
  { to: '/admin/ingest', label: 'Ingest' },
  { to: '/admin/login-log', label: 'Login Log' },
  { to: '/admin/users', label: 'Users' },
];

const FOCUSABLE =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Left slide-over navigation for the mobile header's hamburger.
 *
 * Carries the same destinations as the desktop nav, with the Admin group listed inline
 * (a hover dropdown has no meaning on touch) and the user identity + Sign out pinned to
 * the bottom above the safe-area inset. Closes on navigate, Escape, and backdrop tap;
 * traps focus and restores it to the hamburger on close.
 */
export function MobileNavDrawer({
  open,
  onClose,
  email,
  isAdmin,
  isCrew,
  signOut,
}: MobileNavDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => {
      setEntered(true);
      closeRef.current?.focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      setEntered(false);
      restoreFocusRef.current?.focus?.();
    };
  }, [open]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'Tab') return;
    const nodes = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (!nodes || nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  if (!open) return null;

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center min-h-[48px] px-4 rounded-md text-base font-medium transition-colors ${
      isActive ? 'bg-brand-blue text-white' : 'text-brand-charcoal active:bg-slate-100'
    }`;

  const adminLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center min-h-[44px] pl-7 pr-4 rounded-md text-sm transition-colors ${
      isActive ? 'bg-brand-blue/10 text-brand-blue font-medium' : 'text-brand-charcoal active:bg-slate-100'
    }`;

  return (
    <div className="fixed inset-0 z-50 md:hidden" onKeyDown={onKeyDown}>
      <div
        className={`absolute inset-0 bg-slate-900/40 motion-safe:transition-opacity motion-safe:duration-200 ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className="absolute inset-y-0 left-0 w-[82%] max-w-[320px] bg-white shadow-xl flex flex-col motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out"
        style={{ transform: entered ? 'translateX(0)' : 'translateX(-100%)' }}
      >
        <header className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 pt-[calc(0.75rem+env(safe-area-inset-top))]">
          <span className="text-lg font-bold text-brand-blue">ProjectCalendar</span>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="ml-auto min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-500 text-2xl leading-none"
          >
            ×
          </button>
        </header>

        <nav className="flex-1 overflow-y-auto overscroll-contain px-2 py-2 space-y-1">
          {PRIMARY_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} onClick={onClose}>
              {l.label}
            </NavLink>
          ))}

          {isAdmin && (
            <div className="pt-3">
              <div className="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Admin
              </div>
              {ADMIN_LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} className={adminLinkClass} onClick={onClose}>
                  {l.label}
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        <footer className="border-t border-slate-200 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] space-y-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm text-slate-600 truncate">{email}</span>
            {isCrew && (
              <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-brand-charcoal/10 text-brand-charcoal font-medium">
                Crew
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              onClose();
              void signOut();
            }}
            className="w-full min-h-[44px] rounded-md border border-slate-300 text-brand-blue font-medium active:bg-slate-100"
          >
            Sign out
          </button>
        </footer>
      </div>
    </div>
  );
}
